import { useState, useRef, useEffect, useCallback } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Download, QrCode, Palette, Sparkles, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { QR_STYLES, DEFAULT_QR_STYLE, QR_SHAPES, QR_SHAPE_BY_ID, DEFAULT_QR_SHAPE } from '../lib/qr-styles';
import api from '../lib/api';

const QR_SIZES = [
  { value: '200', label: 'Small (200px)' },
  { value: '300', label: 'Medium (300px)' },
  { value: '400', label: 'Large (400px)' },
  { value: '512', label: 'Extra Large (512px)' },
  { value: '1024', label: 'HD (1024px)' },
];

const PREVIEW_PX = 260;

// Build the config object that qr-code-styling expects. Kept here (not in
// a lib file) because it's tightly coupled to the component's state shape.
function buildQrOptions({
  url,
  sizePx,
  styleId,
  shapeId,
  logoUrl,
  customFgColor,
  customGradient,
}) {
  const style = QR_STYLES[styleId] || QR_STYLES[DEFAULT_QR_STYLE];
  const dotsType = QR_SHAPE_BY_ID[shapeId]?.dotsType || 'square';

  // Resolve the color/gradient to apply to the modules.
  let dotsColor = style.fgColor;
  let dotsGradient = style.gradient || null;
  if (styleId === 'custom') {
    if (customGradient) {
      dotsGradient = customGradient;
      dotsColor = customGradient.from;
    } else {
      dotsColor = customFgColor;
      dotsGradient = null;
    }
  }

  const dotsOptions = dotsGradient
    ? {
        type: dotsType,
        gradient: {
          type: 'linear',
          rotation: 0,
          colorStops: [
            { offset: 0, color: dotsGradient.from },
            { offset: 1, color: dotsGradient.to },
          ],
        },
      }
    : {
        type: dotsType,
        color: dotsColor,
      };

  // Keep corner squares square (finder patterns) — changing them hurts
  // scannability and diverges from the backend PIL renderer.
  const cornersSquareOptions = dotsGradient
    ? {
        type: 'square',
        gradient: {
          type: 'linear',
          rotation: 0,
          colorStops: [
            { offset: 0, color: dotsGradient.from },
            { offset: 1, color: dotsGradient.to },
          ],
        },
      }
    : { type: 'square', color: dotsColor };

  const opts = {
    width: sizePx,
    height: sizePx,
    type: 'canvas',
    data: url,
    margin: 8,
    qrOptions: { errorCorrectionLevel: 'H' },
    dotsOptions,
    cornersSquareOptions,
    backgroundOptions: { color: '#ffffff' },
  };

  // Always set image + imageOptions (even to empty) so update() clears
  // the previous logo rather than leaving it in place via deep-merge.
  opts.image = logoUrl || '';
  opts.imageOptions = logoUrl
    ? {
        hideBackgroundDots: true,
        imageSize: 0.28,
        margin: 2,
        crossOrigin: 'anonymous',
      }
    : { hideBackgroundDots: false, imageSize: 0, margin: 0 };

  return opts;
}

const QRCodeGenerator = ({
  url,
  username,
  defaultStyle,
  defaultShape,
  defaultLogo,
  avatarUrl,
}) => {
  const [selectedStyle, setSelectedStyle] = useState(
    QR_STYLES[defaultStyle] ? defaultStyle : DEFAULT_QR_STYLE,
  );
  const [selectedShape, setSelectedShape] = useState(
    QR_SHAPE_BY_ID[defaultShape] ? defaultShape : DEFAULT_QR_SHAPE,
  );
  const [useLogo, setUseLogo] = useState(Boolean(defaultLogo && avatarUrl));
  const [size, setSize] = useState('300');
  const [customFgColor, setCustomFgColor] = useState('#06b6d4');
  const [customGradientFrom, setCustomGradientFrom] = useState('#06b6d4');
  const [customGradientTo, setCustomGradientTo] = useState('#d946ef');
  const [useGradient, setUseGradient] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCard, setDownloadingCard] = useState(false);
  const [open, setOpen] = useState(false);

  const previewRef = useRef(null);
  const qrRef = useRef(null);
  const currentStyle = QR_STYLES[selectedStyle];

  // Build options for the DOWNLOADED QR (at full requested size).
  const buildDownloadOptions = useCallback(() => buildQrOptions({
    url,
    sizePx: parseInt(size, 10),
    styleId: selectedStyle,
    shapeId: selectedShape,
    logoUrl: useLogo && avatarUrl ? avatarUrl : null,
    customFgColor,
    customGradient: selectedStyle === 'custom' && useGradient
      ? { from: customGradientFrom, to: customGradientTo }
      : null,
  }), [url, size, selectedStyle, selectedShape, useLogo, avatarUrl,
       customFgColor, useGradient, customGradientFrom, customGradientTo]);

  // Build options for the PREVIEW (capped at PREVIEW_PX for layout).
  const buildPreviewOptions = useCallback(() => buildQrOptions({
    url,
    sizePx: PREVIEW_PX,
    styleId: selectedStyle,
    shapeId: selectedShape,
    logoUrl: useLogo && avatarUrl ? avatarUrl : null,
    customFgColor,
    customGradient: selectedStyle === 'custom' && useGradient
      ? { from: customGradientFrom, to: customGradientTo }
      : null,
  }), [url, selectedStyle, selectedShape, useLogo, avatarUrl,
       customFgColor, useGradient, customGradientFrom, customGradientTo]);

  // Mount / update the preview instance when the dialog opens or config changes.
  useEffect(() => {
    if (!open) return;
    const opts = buildPreviewOptions();
    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling(opts);
      // Defer append to after ref mounts
      requestAnimationFrame(() => {
        if (previewRef.current) {
          previewRef.current.innerHTML = '';
          qrRef.current.append(previewRef.current);
        }
      });
    } else {
      qrRef.current.update(opts);
    }
  }, [open, buildPreviewOptions]);

  const handleDownloadPng = useCallback(() => {
    setDownloading(true);
    try {
      const dl = new QRCodeStyling(buildDownloadOptions());
      dl.download({ name: `qr-${username}-${selectedStyle}-${size}px`, extension: 'png' });
      toast.success('QR code downloaded!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to download QR code');
    } finally {
      setDownloading(false);
    }
  }, [buildDownloadOptions, username, selectedStyle, size]);

  const handleDownloadConCard = useCallback(async () => {
    setDownloadingCard(true);
    try {
      const res = await api.get('/creator/con-card', { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${username}-concard.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success('Con card downloaded');
    } catch (e) {
      console.error(e);
      toast.error('Could not generate con card');
    } finally {
      setDownloadingCard(false);
    }
  }, [username]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-white/20 hover:bg-cyan-950/30 text-slate-300 w-full"
          data-testid="qr-code-btn"
        >
          <QrCode className="w-4 h-4 mr-1" />
          <span className="hidden xs:inline">QR</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="glass border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="qr-code-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <QrCode className="w-5 h-5 text-cyan-400" />
            QR Code Generator
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Style your QR code and download a PNG or your printable con card.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          {/* Preview Section */}
          <div className="flex flex-col items-center">
            <Card className="glass border-white/10 p-6 w-full flex flex-col items-center" data-testid="qr-preview">
              <div
                className="bg-white rounded-xl p-2 mb-4 flex items-center justify-center"
                style={{ minWidth: PREVIEW_PX + 16, minHeight: PREVIEW_PX + 16 }}
              >
                <div ref={previewRef} style={{ width: PREVIEW_PX, height: PREVIEW_PX }} />
              </div>

              <p className="text-sm text-slate-400 text-center mb-4">
                Scans to: <span className="text-cyan-400">@{username}</span>
              </p>

              <Button
                onClick={handleDownloadPng}
                disabled={downloading}
                className="w-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400"
                data-testid="download-qr-btn"
              >
                {downloading ? (
                  <span className="animate-pulse">Downloading...</span>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download PNG ({size}px)
                  </>
                )}
              </Button>

              <Button
                onClick={handleDownloadConCard}
                disabled={downloadingCard}
                variant="outline"
                className="w-full mt-2 border-white/20 hover:bg-cyan-950/30"
                data-testid="download-concard-btn"
              >
                {downloadingCard ? (
                  <span className="animate-pulse">Generating...</span>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download Con Card (PDF)
                  </>
                )}
              </Button>
            </Card>
          </div>

          {/* Options Section */}
          <div className="space-y-4">
            {/* Color Style */}
            <div>
              <Label className="text-white flex items-center gap-2 mb-2">
                <Palette className="w-4 h-4 text-fuchsia-400" />
                Color
              </Label>
              <Select value={selectedStyle} onValueChange={setSelectedStyle}>
                <SelectTrigger className="bg-slate-900/50 border-white/10 text-white" data-testid="qr-style-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 max-h-64">
                  {Object.entries(QR_STYLES).map(([key, style]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full border border-white/20"
                          style={{
                            background: style.gradient
                              ? `linear-gradient(135deg, ${style.gradient.from}, ${style.gradient.to})`
                              : style.fgColor,
                          }}
                        />
                        <span>{style.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">{currentStyle.description}</p>
            </div>

            {/* Shape */}
            <div>
              <Label className="text-white flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                Shape
              </Label>
              <Select value={selectedShape} onValueChange={setSelectedShape}>
                <SelectTrigger className="bg-slate-900/50 border-white/10 text-white" data-testid="qr-shape-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {QR_SHAPES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span>{s.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {QR_SHAPE_BY_ID[selectedShape]?.description}
              </p>
            </div>

            {/* Logo toggle */}
            <div>
              <Label className="text-white flex items-center gap-2 mb-2">
                <ImageIcon className="w-4 h-4 text-fuchsia-400" />
                Logo in center
              </Label>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="useLogo"
                  checked={useLogo}
                  disabled={!avatarUrl}
                  onChange={(e) => setUseLogo(e.target.checked)}
                  className="rounded border-white/20"
                  data-testid="qr-logo-toggle"
                />
                <Label htmlFor="useLogo" className={`cursor-pointer ${avatarUrl ? 'text-slate-300' : 'text-slate-500'}`}>
                  {avatarUrl ? 'Embed my avatar in the center' : 'Upload a profile image first'}
                </Label>
              </div>
            </div>

            {/* Size */}
            <div>
              <Label className="text-white mb-2 block">Size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger className="bg-slate-900/50 border-white/10 text-white" data-testid="qr-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {QR_SIZES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom color overrides */}
            {selectedStyle === 'custom' && (
              <Card className="glass border-cyan-500/20 p-4 space-y-4">
                <Label className="text-cyan-400 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Custom Colors
                </Label>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="useGradient"
                    checked={useGradient}
                    onChange={(e) => setUseGradient(e.target.checked)}
                    className="rounded border-white/20"
                  />
                  <Label htmlFor="useGradient" className="text-slate-300 cursor-pointer">
                    Use Gradient
                  </Label>
                </div>

                {useGradient ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-400 text-xs">From Color</Label>
                      <Input
                        type="color"
                        value={customGradientFrom}
                        onChange={(e) => setCustomGradientFrom(e.target.value)}
                        className="h-10 cursor-pointer"
                        data-testid="custom-gradient-from"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">To Color</Label>
                      <Input
                        type="color"
                        value={customGradientTo}
                        onChange={(e) => setCustomGradientTo(e.target.value)}
                        className="h-10 cursor-pointer"
                        data-testid="custom-gradient-to"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label className="text-slate-400 text-xs">QR Code Color</Label>
                    <Input
                      type="color"
                      value={customFgColor}
                      onChange={(e) => setCustomFgColor(e.target.value)}
                      className="h-10 cursor-pointer"
                      data-testid="custom-color-input"
                    />
                  </div>
                )}
              </Card>
            )}

            {/* Info */}
            <div className="p-3 bg-slate-900/50 rounded-lg border border-white/5">
              <p className="text-xs text-slate-400">
                <strong className="text-slate-300">Tip:</strong> The preview below matches what you'll download. Save your choices on the Settings tab to persist them on your con card.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRCodeGenerator;
